// textAngular's `ta-default-wrap`, reproduced as pure string functions (vendor/textAngular 1.5.2,
// src/taBind.js — identical in the dist/textAngular.js careero loads).
//
// ⚠️ THE MODEL IS THE WRAPPED HTML, AND THE MODEL IS WHAT GETS MAILED. Upstream's composer posts
// `this.body` and `this.signature` — the ng-model values — and taBind puts `_ensureContentWrapped`
// first in BOTH pipelines (taBind.js:339-342): `$parsers` (editor → model, on keyup after a 400ms
// debounce and on blur) and `$formatters` (model → editor). Without it the port sent the typed
// string bare, where upstream sends `<div>…</div>`, and the email's block spacing — next to the
// signature especially — came out different.
//
// What taBind does, in order:
//
// - An EMPTY model renders as `_defaultVal`, `<div><br></div>` (taBind.js:154, 963-969), so the
//   first character typed lands INSIDE that div — Chrome never sees a bare top-level text node in
//   the normal path. A keyup that leaves the element blank writes `_defaultVal` back and puts the
//   caret at its start (taBind.js:832-834).
// - A BLANK value is `''` in the model (`_setViewValue`, taBind.js:308-311): `<div><br></div>`,
//   whitespace between tags, nothing at all — `_taBlankTest` (taBind.js:2-27) decides.
// - Anything else goes through `_ensureContentWrapped` (taBind.js:171-221), below.
//
// No DOM here on purpose: the repo has no DOM test environment, so the top-level node split that
// taBind gets from `angular.element('<div>' + value + '</div>')` is a small tokenizer instead. Its
// input is an element's `innerHTML` — browser-serialised, so well-formed — or server-authored HTML.

/** globals.js:36 — `li`, `span`, `br`, `img` are NOT here, which is what makes them wrap. */
const BLOCK_ELEMENTS =
  /^(address|article|aside|audio|blockquote|canvas|dd|div|dl|fieldset|figcaption|figure|footer|form|h1|h2|h3|h4|h5|h6|header|hgroup|hr|noscript|ol|output|p|pre|section|table|tfoot|ul|video)$/i

/** taBind.js:3 */
const INLINE_TAGS_NONBLANK =
  /<(a|abbr|acronym|bdi|bdo|big|cite|code|del|dfn|img|ins|kbd|label|map|mark|q|ruby|rp|rt|s|samp|time|tt|var)[^>]*(>|$)/i

const VOID_ELEMENTS = /^(area|base|br|col|embed|hr|img|input|link|meta|param|source|track|wbr)$/i
const RAW_TEXT_ELEMENTS = /^(script|style|textarea|title)$/i

/** `_defaultVal` for a non-IE browser (taBind.js:154-155): what an empty editor holds. */
export function taDefaultValue(wrap: string): string {
  return `<${wrap}><br></${wrap}>`
}

/** `_taBlankTest(_defaultTest)` (taBind.js:2-27), line for line. */
export function taIsBlank(value: string | null | undefined, wrap: string): boolean {
  if (value === null || value === undefined || value === '') return true
  let blank = value
  const firstMatch = /(^[^<]|>)[^<]/i.exec(blank)
  let firstTagIndex: number
  if (firstMatch === null) {
    // Only the first four attribute values, as upstream — "waaayy too long" otherwise.
    blank = blank.replace(/="[^"]*"/i, '').replace(/="[^"]*"/i, '').replace(/="[^"]*"/i, '').replace(/="[^"]*"/i, '')
    firstTagIndex = blank.indexOf('>')
  } else {
    firstTagIndex = firstMatch.index
  }
  blank = blank.trim().substring(firstTagIndex, firstTagIndex + 100)
  if (/^[^<>]+$/i.test(blank)) return false
  if (blank.length === 0 || blank === taDefaultValue(wrap) || /^>(\s|&nbsp;)*<\/[^>]+>$/i.test(blank)) return true
  if (/>\s*[^\s<]/i.test(blank) || INLINE_TAGS_NONBLANK.test(blank)) return false
  return true
}

type TopLevelNode =
  | { kind: 'text'; source: string }
  | { kind: 'comment'; source: string; data: string }
  | { kind: 'element'; source: string; name: string }

/** The end of the tag that opens at `start` (`<`), quotes respected; the string's end if unclosed. */
function tagEnd(html: string, start: number): number {
  let quote: string | null = null
  for (let i = start + 1; i < html.length; i++) {
    const ch = html[i]
    if (quote !== null) {
      if (ch === quote) quote = null
    } else if (ch === '"' || ch === "'") {
      quote = ch
    } else if (ch === '>') {
      return i + 1
    }
  }
  return html.length
}

/** `domTest[0].childNodes` — the top-level nodes of `html`, each with the markup it came from. */
export function topLevelNodes(html: string): TopLevelNode[] {
  const nodes: TopLevelNode[] = []
  let i = 0
  let textStart = 0
  const flushText = (end: number): void => {
    if (end > textStart) nodes.push({ kind: 'text', source: html.slice(textStart, end) })
  }
  while (i < html.length) {
    if (html[i] !== '<') {
      i++
      continue
    }
    if (html.startsWith('<!--', i)) {
      flushText(i)
      const close = html.indexOf('-->', i + 4)
      const end = close === -1 ? html.length : close + 3
      nodes.push({ kind: 'comment', source: html.slice(i, end), data: html.slice(i + 4, close === -1 ? html.length : close) })
      i = textStart = end
      continue
    }
    if (html[i + 1] === '/' && /[a-z]/i.test(html[i + 2] ?? '')) {
      // A close tag with nothing open: the parser drops it, so it is no node at all.
      flushText(i)
      i = textStart = tagEnd(html, i)
      continue
    }
    const open = /^<([a-z][^\s/>]*)/i.exec(html.slice(i))
    if (open === null) {
      i++ // a `<` that starts no tag is text
      continue
    }
    flushText(i)
    const name = (open[1] ?? '').toLowerCase()
    let end = tagEnd(html, i)
    if (RAW_TEXT_ELEMENTS.test(name)) {
      const close = html.toLowerCase().indexOf(`</${name}`, end)
      end = close === -1 ? html.length : tagEnd(html, close)
    } else if (!VOID_ELEMENTS.test(name)) {
      // Walk to the matching close tag, counting every element opened and closed on the way.
      let depth = 1
      let j = end
      while (depth > 0 && j < html.length) {
        const next = html.indexOf('<', j)
        if (next === -1) {
          j = html.length
          break
        }
        if (html.startsWith('<!--', next)) {
          const close = html.indexOf('-->', next + 4)
          j = close === -1 ? html.length : close + 3
          continue
        }
        const tag = /^<(\/?)([a-z][^\s/>]*)/i.exec(html.slice(next))
        if (tag === null) {
          j = next + 1
          continue
        }
        const tagName = (tag[2] ?? '').toLowerCase()
        j = tagEnd(html, next)
        if (tag[1] === '/') depth--
        else if (RAW_TEXT_ELEMENTS.test(tagName)) {
          const close = html.toLowerCase().indexOf(`</${tagName}`, j)
          j = close === -1 ? html.length : tagEnd(html, close)
        } else if (!VOID_ELEMENTS.test(tagName)) depth++
      }
      end = j
    }
    nodes.push({ kind: 'element', source: html.slice(i, end), name })
    i = textStart = end
  }
  flushText(html.length)
  return nodes
}

const NAMED_ENTITIES: Record<string, string> = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ' }

/**
 * A text node's `textContent` — its markup DECODED. Upstream concatenates that straight back into
 * HTML (taBind.js:197-204), so `a &amp; b` beside a block comes out as `<div>a & b</div>`: a
 * textAngular quirk, reproduced because it is what upstream mails. Covers the entities a
 * contenteditable serialises (`&amp; &lt; &gt; &nbsp;`), the other XML ones and numeric references;
 * any other named entity is left as written.
 */
function textContent(source: string): string {
  return source.replace(/&(#x[0-9a-f]+|#[0-9]+|[a-z]+);/gi, (match, ref: string) => {
    if (ref[0] === '#') {
      const code = ref[1] === 'x' || ref[1] === 'X' ? parseInt(ref.slice(2), 16) : parseInt(ref.slice(1), 10)
      return Number.isNaN(code) || code > 0x10ffff ? match : String.fromCodePoint(code)
    }
    return NAMED_ENTITIES[ref.toLowerCase()] ?? match
  })
}

/**
 * `_ensureContentWrapped` (taBind.js:171-221). Blank → unchanged. No element children, or none of
 * them a block → the WHOLE value in one `<wrap>` (so `a<br>b` stays one block). Otherwise each
 * top-level node in turn: blocks and comments as they are, whitespace-only text as it is, any
 * other text and every non-block element (a `<br>` included) in a `<wrap>` of its own.
 */
export function ensureContentWrapped(value: string, wrap: string): string {
  if (taIsBlank(value, wrap)) return value
  const nodes = topLevelNodes(value)
  const wrapped = (inner: string): string => `<${wrap}>${inner}</${wrap}>`
  if (!nodes.some((node) => node.kind === 'element' && BLOCK_ELEMENTS.test(node.name))) return wrapped(value)
  let out = ''
  for (const node of nodes) {
    if (node.kind === 'comment') {
      out += `<!--${node.data}-->`
    } else if (node.kind === 'text') {
      const text = textContent(node.source)
      out += text.trim() === '' ? text : wrapped(text)
    } else if (!BLOCK_ELEMENTS.test(node.name)) {
      out += node.source.trim() === '' ? node.source : wrapped(node.source)
    } else {
      out += node.source
    }
  }
  return out
}

/**
 * Editor → model: `_setViewValue` then the `$parsers` (taBind.js:304-319, 339). What the editor
 * reports, and so what Send posts. Firefox's lone `<br>` counts as empty (taBind.js:827-830).
 */
export function taModelFromEditor(innerHtml: string, wrap: string): string {
  return taIsBlank(innerHtml, wrap) ? '' : ensureContentWrapped(innerHtml, wrap)
}

/** Model → editor: the `$formatters`, then `$render`'s empty case (taBind.js:342, 963-969). */
export function taEditorFromModel(model: string, wrap: string): string {
  return model === '' ? taDefaultValue(wrap) : ensureContentWrapped(model, wrap)
}
